import { renderMarkdown } from "@apsu/shared";
import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

type ChangelogEntry = {
	id: string;
	title: string;
	slug: string;
	body: string;
	version: string | null;
	publishedAt: string | null;
	category: string;
	createdAt: string;
	updatedAt: string;
};

export function AdminChangelogFormPage() {
	const { slug } = useParams<{ slug: string }>();
	const navigate = useNavigate();
	const isEdit = !!slug;

	const [title, setTitle] = useState("");
	const [body, setBody] = useState("");
	const [category, setCategory] = useState("feature");
	const [version, setVersion] = useState("");
	const [published, setPublished] = useState(false);
	const [entryId, setEntryId] = useState<string | null>(null);

	const [tab, setTab] = useState<"write" | "preview">("write");
	const [loading, setLoading] = useState(false);
	const [fetchLoading, setFetchLoading] = useState(isEdit);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!slug) return;

		async function loadEntry() {
			try {
				const res = await fetch(`/api/admin/changelog/${slug}`, {
					credentials: "include",
				});
				if (!res.ok) {
					setError(`Failed to load entry (${res.status})`);
					return;
				}
				const json = (await res.json()) as { data: ChangelogEntry };
				const entry = json.data;
				setTitle(entry.title);
				setBody(entry.body);
				setCategory(entry.category);
				setVersion(entry.version ?? "");
				setPublished(!!entry.publishedAt);
				setEntryId(entry.id);
			} catch {
				setError("Failed to load entry");
			} finally {
				setFetchLoading(false);
			}
		}

		loadEntry();
	}, [slug]);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setLoading(true);
		setError(null);

		const payload = {
			title,
			body,
			category,
			version: version || undefined,
			publishedAt: published ? new Date().toISOString() : null,
		};

		try {
			const url = isEdit
				? `/api/admin/changelog/${entryId}`
				: "/api/admin/changelog";
			const method = isEdit ? "PUT" : "POST";

			const res = await fetch(url, {
				method,
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});

			if (!res.ok) {
				const json = (await res.json().catch(() => null)) as {
					error?: string;
				} | null;
				setError(json?.error ?? `Request failed (${res.status})`);
				return;
			}

			navigate("/admin/changelog");
		} catch {
			setError("An unexpected error occurred");
		} finally {
			setLoading(false);
		}
	}

	if (fetchLoading) {
		return <p className="text-gray-500 text-sm">Loading…</p>;
	}

	return (
		<div>
			<h1 className="text-2xl font-bold text-gray-900 mb-4">
				{isEdit ? "Edit Entry" : "New Entry"}
			</h1>

			{error && <p className="text-red-600 text-sm mb-3">{error}</p>}

			<form onSubmit={handleSubmit} className="space-y-4 max-w-2xl">
				<div>
					<label
						htmlFor="title"
						className="block text-sm font-medium text-gray-700 mb-1"
					>
						Title
					</label>
					<input
						id="title"
						type="text"
						required
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
					/>
				</div>

				<div>
					<label
						htmlFor="category"
						className="block text-sm font-medium text-gray-700 mb-1"
					>
						Category
					</label>
					<select
						id="category"
						value={category}
						onChange={(e) => setCategory(e.target.value)}
						className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
					>
						<option value="feature">Feature</option>
						<option value="fix">Fix</option>
						<option value="improvement">Improvement</option>
					</select>
				</div>

				<div>
					<label
						htmlFor="version"
						className="block text-sm font-medium text-gray-700 mb-1"
					>
						Version (optional)
					</label>
					<input
						id="version"
						type="text"
						value={version}
						onChange={(e) => setVersion(e.target.value)}
						className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
						placeholder="e.g. 1.2.0"
					/>
				</div>

				<div>
					<div className="flex gap-2 mb-1">
						<span className="block text-sm font-medium text-gray-700">
							Body
						</span>
						<button
							type="button"
							onClick={() => setTab("write")}
							className={`text-xs px-2 py-0.5 rounded ${tab === "write" ? "bg-gray-200 font-medium" : "text-gray-500 hover:text-gray-700"}`}
						>
							Write
						</button>
						<button
							type="button"
							onClick={() => setTab("preview")}
							className={`text-xs px-2 py-0.5 rounded ${tab === "preview" ? "bg-gray-200 font-medium" : "text-gray-500 hover:text-gray-700"}`}
						>
							Preview
						</button>
					</div>
					{tab === "write" && (
						<textarea
							id="body"
							required
							value={body}
							onChange={(e) => setBody(e.target.value)}
							rows={12}
							className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
						/>
					)}
					{tab === "preview" && (
						<div
							className="w-full border border-gray-300 rounded px-3 py-2 text-sm min-h-[12rem] [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-semibold [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic"
							// biome-ignore lint/security/noDangerouslySetInnerHtml: markdown is sanitized by rehype-sanitize
							dangerouslySetInnerHTML={{ __html: renderMarkdown(body) }}
						/>
					)}
				</div>

				<div className="flex items-center gap-2">
					<input
						id="published"
						type="checkbox"
						checked={published}
						onChange={(e) => setPublished(e.target.checked)}
					/>
					<label htmlFor="published" className="text-sm text-gray-700">
						Published
					</label>
				</div>

				<div className="flex gap-2">
					<button
						type="submit"
						disabled={loading}
						className="text-sm px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
					>
						{loading ? "Saving…" : isEdit ? "Update" : "Create"}
					</button>
					<button
						type="button"
						onClick={() => navigate("/admin/changelog")}
						className="text-sm px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
					>
						Cancel
					</button>
				</div>
			</form>
		</div>
	);
}
